/** @format */

import { observer } from "mobx-react-lite";
import React, { useState, useMemo } from "react";
import styled from "styled-components";
import { PageProps } from "../types";
import { useStores } from "../../models";
import useGlobalTheme from "../../hooks/useGlobalTheme";
import useNavigation from "../../hooks/useNavigation";
import { rules } from "../../refactor-utils/validator";
import PageWrap from "../components/PageWrap";
import NormalInput from "../../components/NormalInput";
import NormalInputCode from "../../components/NormalInputCode";
import NormalButton from "../../components/NormalButton";
import CheckBox from "../../components/CheckBox";
import PrivacyDialog from "../../components/PrivacyDialog";
import { ToastError } from "../../components/Toast";
import ServiceContractDialog from "../../components/ServiceContractDialog";
const registerLogo = require("../../assets/images/login-logo.png");

const Page = styled.div<{ isMobile: boolean }>`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 100%;
  background-color: ${({ theme, isMobile }) =>
    isMobile ? theme.colors.primary : theme.colors.secondary};
`;

const FormWrap = styled.div<{ isMobile: boolean }>`
  display: flex;
  flex-direction: column;
  width: ${({ isMobile }) => `${isMobile ? 100 : 60}%`};
  max-width: 500px;
  min-width: 320px;
  min-height: 400px;
  box-shadow: 5px 5px 5px rgba(0, 0, 0, 0.2);
`;

const TopArea = styled.div`
  position: relative;
  width: 100%;
  height: 160px;
  background-color: ${({ theme }) => theme.colors.primary};
  border-radius: ${({ theme }) => theme.radii[3]};
  border-bottom-right-radius: 0px;
  border-bottom-left-radius: 0px;
`;

const Form = styled.div`
  overflow-y: scroll;
  display: flex;
  width: 100%;
  height: 480px;
  max-height: 480px;
  flex-direction: column;
  padding: ${({ theme }) => theme.space[4]};
  border: 1px solid ${({ theme }) => theme.colors.border};
  background-color: ${({ theme }) => theme.colors.blockBackground};
  border-radius: ${({ theme }) => theme.radii[3]};
  border-top-right-radius: 0px;
  border-top-left-radius: 0px;
`;

const RegisterLogo = styled.img`
  position: absolute;
  z-index: 2;
  top: ${({ theme }) => theme.space[2]};
  right: ${({ theme }) => theme.space[2]};
  width: 120px;
  height: 120px;
`;

const MainTitle = styled.h1`
  font-size: 1.6rem;
  color: ${({ theme }) => theme.colors.blockBackground};
  margin-top: ${({ theme }) => theme.space[4]};
  margin-left: ${({ theme }) => theme.space[4]};
  margin-bottom: ${({ theme }) => theme.space[2]};
`;

const Title = styled.h1`
  font-size: 1.3rem;
  margin: 0px;
  margin-bottom: ${({ theme }) => theme.space[2]};
`;

const ButtonArea = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  margin-bottom: 10px;
`;

const AgreeArea = styled(ButtonArea)`
  justify-content: flex-start;
`;

const Text = styled.span<{ color?: string; fontSize?: string }>`
  color: ${({ color, theme }) => color || theme.colors.text};
  font-size: ${({ fontSize }) => fontSize || "0.8rem"};
`;
const Tabs = styled.div`
 display: flex;
  flex-direction: row;
  border-radius: 6px;
  margin: 10px 0 20px 0;
  background-color: #e6e7f2;
`
const TabItem = styled.div<{ active: boolean }>`
  flex: 1;
  height: 32px;
  line-height: 32px;
  border-radius: 6px;
  justify-content: center;
  align-items: center;
  font-size: 14px;
  color: ${({ active, theme }) => (active ? theme.colors.background : theme.colors.text)};
  font-weight: 400;
  text-align: center;
  background-color: ${({ active, theme }) => (active ? theme.colors.primary : "#E6E7F2")};
  cursor: pointer;
`
const tabs: string[] = ["账号注册", "手机注册"]
interface Props extends PageProps {}

const Register: React.FC<Props> = observer(() => {
  const theme = useGlobalTheme();
  const { navigate } = useNavigation();
  const { authStore, isMobile, configStore } = useStores();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [code, setCode] = useState("");
  const [isAgreePrivacy, setIsAgreePrivacy] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isPrivacyModalVisible, setIsPrivacyModalVisible] = useState(false);
  const [isServiceContractModalVisible, setIsServiceContractModalVisible] =
    useState(false);
  const [tab, setTab] = useState("账号注册")

  const onTabPress = (item: string) => {
    if (tab !== item) {
      setUsername("")
      setPassword("")
      setEmail("")
      setCode("")
      setPhone("")
      setTab(item)
    }
  }
  async function onRegister() {
    setLoading(true);
    try {
      const params: any = {}
      if (tab === "手机注册") {
        params.type = 1
        params.name = phone
        params.phone = phone
        params.username = phone
        params.password = password
        params.code = code
        params.country_code = '86'
      } else  {
        params.name = username
        params.username = username
        params.password = password
        params.email = email
      }
      await authStore.httpRegister(params);
    } catch (e) {
      ToastError(e.message);
    } finally {
      setLoading(false);
    }
  }

  const active = useMemo(() => {
    if(tab === "手机注册") {
      return phone && code && password && isAgreePrivacy
    }
    return username && password && email && isAgreePrivacy
  }, [tab, username, password, phone, code, email, isAgreePrivacy])

  return (
    <PageWrap>
      <Page isMobile={isMobile}>
        <FormWrap isMobile={isMobile}>
          <TopArea>
            <MainTitle>欢迎來到，</MainTitle>
            <MainTitle>{configStore.appName}IM</MainTitle>
            <RegisterLogo src={registerLogo} />
          </TopArea>
          <Form>
            <Title>注册</Title>
            <Tabs>
              {tabs.map((item, index) => (
                <TabItem active={item === tab} onClick={() => onTabPress(item)} key={index}>
                  {item}
                </TabItem>
              ))}
            </Tabs>
            {tab === '账号注册' && (
              <>
                 <NormalInput
                  label="账号"
                  placeholder="请输入账号"
                  value={username}
                  validateRule={rules.username}
                  onChange={(e) => {
                    setUsername(e.target.value);
                  }}
                />
                <NormalInput
                  type="password"
                  label="密码"
                  placeholder="请输入密码"
                  value={password}
                  validateRule={rules.password}
                  onChange={(e) => {
                    setPassword(e.target.value);
                  }}
                />
                <NormalInput
                  label="电子邮箱"
                  placeholder="请输入电子邮箱"
                  value={email}
                  validateRule={rules.email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                  }}
                />
              </>
            )}

             {tab === '手机注册' && (
              <>
                 <NormalInput
                  label="手机号"
                  placeholder="请输入手机号"
                  value={phone}
                  validateRule={rules.phone}
                  onChange={(e) => {
                    setPhone(e.target.value);
                  }}
                />
                <NormalInputCode
                  label="手机验证码"
                  value={code}
                  name={phone}
                  validateRule={rules.code}
                  onChange={(e) => {
                    setCode(e.target.value);
                  }}
                />
                <NormalInput
                  type="password"
                  label="密码"
                  placeholder="请输入密码"
                  value={password}
                  validateRule={rules.password}
                  onChange={(e) => {
                    setPassword(e.target.value);
                  }}
                />
                
              </>
            )}
           
            <AgreeArea>
              <CheckBox
                checked={isAgreePrivacy}
                value={true}
                onPress={() => setIsAgreePrivacy(!isAgreePrivacy)}
              />
              <Text>我已阅读并同意</Text>
              <Text
                style={{ cursor: "pointer" }}
                color={theme.colors.primary}
                onClick={() => {
                  setIsServiceContractModalVisible(true);
                }}
              >
                《服务协议》
              </Text>
              <Text>和</Text>
              <Text
                style={{ cursor: "pointer" }}
                color={theme.colors.primary}
                onClick={() => {
                  setIsPrivacyModalVisible(true);
                }}
              >
                《隐私政策》
              </Text>
            </AgreeArea>
            <ButtonArea>
              <NormalButton
                disabled={!active || loading}
                onClick={onRegister}
              >
                立即创建
              </NormalButton>
            </ButtonArea>
            <ButtonArea>
              <Text>已有账号？</Text>
              <Text
                style={{ cursor: "pointer" }}
                color={theme.colors.primary}
                onClick={() => {
                  navigate("Login");
                }}
              >
                去登录
              </Text>
            </ButtonArea>
          </Form>
        </FormWrap>
        <ServiceContractDialog
          visible={isServiceContractModalVisible}
          setVisible={setIsServiceContractModalVisible}
          setAgree={setIsAgreePrivacy}
        ></ServiceContractDialog>
        <PrivacyDialog
          visible={isPrivacyModalVisible}
          setVisible={setIsPrivacyModalVisible}
          setAgree={setIsAgreePrivacy}
        ></PrivacyDialog>
      </Page>
    </PageWrap>
  );
});
export default Register;
